import {Avatar} from 'platform/components';
import {
  Box,
  Center,
  Clickable,
  Heading,
  Hide,
  HStack,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetUserQuery} from '@dms/api/customer';
import {BusinessCaseActivityResponseBody} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {ServiceCaseActivityResponseBody} from '@dms/api/workshop';
import {
  ActivityDate,
  ActivitySidebarLink,
  getActivityIconColor,
  transformActivityItem,
} from '@dms/features/activity-panel';
import {openEditTaskFormDialog} from '@dms/features/tasks';
import {getNaturalPersonFullName, useCanOpenTelephonyCall} from '@dms/shared';

import {DOT_CHARACTER} from 'shared/web';

interface ServiceCaseActivityItemProps {
  item: ServiceCaseActivityResponseBody | BusinessCaseActivityResponseBody;
  contactName?: string | null;
  serviceCaseNumber?: string | null;
  serviceOrders: {serviceOrderId: string; number: string}[];
  isLastItem: boolean;
}

export function ServiceCaseActivityItem(props: ServiceCaseActivityItemProps) {
  const {data: userData} = useGetUserQuery(
    {id: props.item.userId ?? ''},
    {skip: !props.item.userId || isNotNilOrEmpty(props.item.userName)}
  );

  const {data: taskAssigneeData} = useGetUserQuery(
    {id: props.item.relatedTask?.assignedTo ?? ''},
    {
      skip:
        !props.item.relatedTask ||
        (props.item.activityType !== 'TASK_FINISHED' && props.item.activityType !== 'TASK_CREATED'),
    }
  );

  const userNameOverride = match(props.item.activityType)
    .with('TASK_FINISHED', 'TASK_CREATED', () =>
      taskAssigneeData?.firstName && taskAssigneeData?.lastName
        ? `${taskAssigneeData.firstName} ${taskAssigneeData.lastName}`
        : undefined
    )
    .otherwise(() => undefined);

  const serviceOrderId = 'serviceOrderId' in props.item ? props.item.serviceOrderId : null;
  const serviceOrderNumber = isNotNilOrEmpty(serviceOrderId)
    ? props.serviceOrders.find((order) => order.serviceOrderId === serviceOrderId)?.number
    : props.serviceCaseNumber;

  const canOpenCall = useCanOpenTelephonyCall();
  const transformedItem = transformActivityItem(
    props.item,
    undefined,
    {
      canOpenCall,
      message: props.item.relatedMessage?.sentTo ?? props.contactName ?? undefined,
      serviceOrderNumber: serviceOrderNumber ?? undefined,
      userName: userNameOverride,
    },
    EntityResourceIds.serviceCase,
    (id) => openEditTaskFormDialog({id, 'data-testid': 'activity-panel-task-edit'})
  );

  if (!transformedItem) {
    return null;
  }

  const resolvedUserName = isNotNilOrEmpty(transformedItem.userName)
    ? transformedItem.userName
    : getNaturalPersonFullName(userData);
  const metadataItems = [transformedItem.interestType, resolvedUserName].filter(isNotNilOrEmpty);

  return (
    <HStack spacing={2} data-testid="activity-item">
      <VStack>
        <Avatar
          color={transformedItem.color}
          icon={transformedItem.icon}
          iconColor={getActivityIconColor(transformedItem.color)}
          variant="square"
        />
        <Hide when={props.isLastItem}>
          <Center width="100%" height="100%">
            <Box height="100%" backgroundColor="general.separator" width="0.125rem" />
          </Center>
        </Hide>
      </VStack>
      <VStack width="100%">
        <Box paddingBottom={4}>
          <VStack spacing={1}>
            <HStack width="100%">
              <VStack width="100%" spacing={1}>
                <Clickable
                  onClick={transformedItem.onClick}
                  isDisabled={!transformedItem.isEditable}
                >
                  <Heading size={5}>{transformedItem.title}</Heading>
                </Clickable>
                <Show when={isNotNilOrEmpty(metadataItems)}>
                  <Text color="tertiary" size="xSmall">
                    <HStack align="center" wrap>
                      {metadataItems.map((item, index) => (
                        <span
                          key={`${item}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                          css={css`
                            display: inline-flex;
                            align-items: center;
                            white-space: nowrap;
                          `}
                        >
                          {item}
                          <Show when={index < metadataItems.length - 1}>
                            <>
                              <Space horizontal={1} />
                              {DOT_CHARACTER}
                              <Space horizontal={1} />
                            </>
                          </Show>
                        </span>
                      ))}
                    </HStack>
                  </Text>
                </Show>
                <Show when={isNotNilOrEmpty(transformedItem.description)}>
                  <Box maxWidth={62}>
                    <Text size="xSmall" color="secondary" noWrap>
                      {transformedItem.description}
                    </Text>
                  </Box>
                </Show>
                <Show when={isNotNilOrEmpty(transformedItem.links)}>
                  <HStack>
                    {transformedItem.links?.map((link) => (
                      <ActivitySidebarLink key={link.label} {...link} />
                    ))}
                  </HStack>
                </Show>
              </VStack>

              <ActivityDate timestamp={props.item.timestamp} />
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </HStack>
  );
}
